import React from "react";
import { createPortal } from "react-dom";
import { useExitAnim } from "../../hooks/useExitAnim";

interface SetterMethodologyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SetterMethodologyModal: React.FC<SetterMethodologyModalProps> = ({
  isOpen,
  onClose,
}) => {
  // Keep the modal mounted through the 200ms exit so anim-modal-out plays.
  const { mounted, closing } = useExitAnim(isOpen, 200);

  if (!mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className={`${closing ? "anim-backdrop-out" : "anim-backdrop-in"} absolute inset-0 bg-black/40 backdrop-blur-sm`}
        onClick={onClose}
      />
      {/* anim-modal-in on the NON-glass wrapper (glass panel can't take transforms) */}
      <div className={`${closing ? "anim-modal-out" : "anim-modal-in"} w-full max-w-lg`}>
        <div className="relative detail-overlay rounded-xl shadow-2xl w-full max-h-[80vh] overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.08]">
            <h2 className="token-18 text-glass">Methodology</h2>
            <button
              onClick={onClose}
              className="anim-tap text-glass-muted hover:text-glass-secondary transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="p-6 overflow-y-auto max-h-[calc(80vh-64px)] space-y-4 token-14 text-glass-secondary">
            <div>
              <h3 className="font-semibold text-glass mb-1">Calls Pitched</h3>
              <p>
                Distinct conversations where the setter sent a message containing a Calendly link.
                One convo = one pitch, regardless of how many times the link was sent.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-glass mb-1">Pre-Calls</h3>
              <p>Sales pre-call transactions attributed to this setter via profile mapping.</p>
            </div>
            {/* The five reply-time numbers on this page, documented together. Three of
                them (Setter Reply Time, Engaged Response, Lead Reply Time) shipped long
                before the other two, which is how a correct build kept getting filed as a
                bug: a viewer compared the conversation modal's number to the scorecard's
                and found them different. They ARE different, on purpose. The wrapper's
                own space-y-4 is load-bearing — the parent only spaces its DIRECT children,
                so re-parenting five of them under one node collapses the gaps without it.
                Do NOT extend this wrapper to Reply Turns below; R1 counts the h3s inside
                it and expects exactly five. */}
            <div data-testid="methodology-reply-group" className="space-y-4">
              <div>
                <h3 className="font-semibold text-glass mb-1">Setter Reply Time</h3>
                <p>
                  Median time from a lead message to the setter's next reply, across all
                  conversations. Offline hours (5+ hour gaps between sessions) are subtracted so
                  overnight gaps don't inflate the number.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-glass mb-1">Engaged Response</h3>
                <p>
                  Median reply time when a lead messages during an active session (setter already
                  has phone in hand). Should be under 5 minutes. Only counts lead messages that
                  arrived while the setter was actively messaging.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-glass mb-1">Lead Reply Time</h3>
                <p>
                  Median wall-clock time leads take to reply after a setter message. Not
                  setter-controllable — indicates lead engagement quality.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-glass mb-1">
                  Wait (Reply Timeline, conversation modal)
                </h3>
                <p>
                  Raw wall-clock wait for one conversation, across its whole history. No offline
                  subtraction, no exclusions. Will not match Setter Reply Time above, by design.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-glass mb-1">Wait (row badge, Timeline tab)</h3>
                <p>
                  Raw wall-clock wait per message, measured from the lead's most recent message. No
                  offline subtraction, no exclusions. Shown as the blue badge on each Timeline row.
                </p>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-glass mb-1">Reply Turns</h3>
              <p>
                Conversation depth by setter reply count: No Reply (0), Light (1), Engaged (2-3),
                Deep (4+). A "reply turn" = setter replied after a lead message. Higher engaged/deep
                ratio = better conversation quality.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-glass mb-1">Follow-up Persistence</h3>
              <p>
                For each conversation where the setter followed up after the lead stopped replying,
                how many calendar days did they keep trying? Buckets: 1d through 7d, then 7+d. More
                days = more persistent. Only counts days after the lead's last reply (or from the
                start if the lead never replied).
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-glass mb-1">DM→Pre-Call %</h3>
              <p>
                Conversion rate from "setter pitched a call" (sent Calendly link) to "lead filled
                the pre-call form." Formula: pre-calls filled ÷ calls pitched × 100.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-glass mb-1">Messaging</h3>
              <p className="mb-2">
                Total outbound messages sent (ManyChat excluded). The /hr rate divides by total work
                hours.
              </p>
              <p className="mb-2">
                <strong className="text-glass">Words per reply</strong> — average words in a
                setter's full response to a lead. Divides total words by reply turns, not by
                messages, so a multi-message burst counts as one answer. Higher = thoughtful
                paragraphs. Lower = rapid-fire fragments. Pair this with Msgs per turn to see if the
                length is one long message or several short ones.
              </p>
              <p>
                <strong className="text-glass">Msgs per turn</strong> — when a lead pings, how many
                messages the setter batches in response. A reply turn = setter message immediately
                after a lead message (matches the canonical reply-turn definition in
                compute_conversation_metadata). 1.0 = one-and-done. 3.0+ = aggressive multi-message
                bursts. Shows "all outbound" when the setter sent messages but had zero
                lead-prompted reply turns today (cold outreach mode).
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-glass mb-1">Sessions</h3>
              <p>
                A session = continuous messaging with gaps under 3 minutes. The clock chart shows
                when the setter was active throughout the day.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
